import DataTable from "@/components/DataTable";
import TradeToolShell, { type ToolCategory } from "@/components/TradeToolShell";
import { DuctSizingCalc, EquivalentDuct, CfmCalc, VelocityNoiseCalc } from "@/components/ductwork/SizingTools";
import { FrictionLossCalc, TotalEffectiveLengthCalc, DuctHeatCalc, DuctLeakageCalc, DuctInsulationCalc } from "@/components/ductwork/PressureTools";
import { DuctWeightCalc, ExpansionCalc, HangerSpacingCalc } from "@/components/ductwork/PropertyTools";
import { maxCfmHeaders, maxCfmRows, fittingEqHeaders, fittingEqRows, cfmRoomHeaders, cfmRoomRows } from "@/components/ductwork/duct-reference-data";
import { SuperheatCalculator, SubcoolingCalculator, LineSizingCalculator, ChargeEstimator, TempPressureLookup } from "@/components/ductwork/RefrigerantTools";
import { ptChartHeaders, ptChartRows, lineSizingHeaders, lineSizingRows } from "@/components/ductwork/refrigerant-data";
import { PsychrometricCalc, CoilSizingCalc, CondensateDrainCalc, DuctAreaCalc } from "@/components/ductwork/DuctExtras";
import TradeFormulas from "@/components/TradeFormulas";

const DUCT_CATEGORIES: ToolCategory[] = [
  {
    id: "sizing", label: "Sizing", color: "bg-primary",
    tools: [
      { value: "sizing", label: "Duct Size" },
      { value: "equivalent", label: "Equiv" },
      { value: "cfm", label: "CFM Calc" },
      { value: "velocity-nc", label: "Velocity" },
      { value: "ductarea", label: "Area" },
      { value: "capacity", label: "Max CFM" },
    ],
  },
  {
    id: "pressure", label: "Pressure", color: "bg-primary",
    tools: [
      { value: "friction", label: "Friction" },
      { value: "tel", label: "TEL" },
      { value: "fittings", label: "Eq. Len" },
      { value: "heat", label: "Heat" },
      { value: "leakage", label: "Leakage" },
      { value: "insulation", label: "R-Value" },
    ],
  },
  {
    id: "properties", label: "Properties", color: "bg-primary",
    tools: [
      { value: "weight", label: "Weight" },
      { value: "expansion", label: "Expand" },
      { value: "hangers", label: "Hangers" },
      { value: "psychro", label: "Psychro" },
      { value: "coil", label: "Coil BTU" },
      { value: "condensate", label: "Condens." },
      { value: "room-cfm", label: "Room CFM" },
      { value: "formulas", label: "Formulas" },
    ],
  },
  {
    id: "hvacr", label: "HVAC/R", color: "bg-copper",
    tools: [
      { value: "superheat", label: "SH" },
      { value: "subcooling", label: "SC" },
      { value: "linesizing", label: "Lines" },
      { value: "charge", label: "Charge" },
      { value: "tplookup", label: "T↔P" },
      { value: "ptchart", label: "P-T Chart" },
      { value: "linetbl", label: "Line Tbl" },
    ],
  },
];

const renderTool = (activeTool: string) => {
  switch (activeTool) {
    case "sizing": return <DuctSizingCalc />;
    case "equivalent": return <EquivalentDuct />;
    case "cfm": return <CfmCalc />;
    case "friction": return <FrictionLossCalc />;
    case "tel": return <TotalEffectiveLengthCalc />;
    case "heat": return <DuctHeatCalc />;
    case "leakage": return <DuctLeakageCalc />;
    case "velocity-nc": return <VelocityNoiseCalc />;
    case "weight": return <DuctWeightCalc />;
    case "insulation": return <DuctInsulationCalc />;
    case "expansion": return <ExpansionCalc />;
    case "hangers": return <HangerSpacingCalc />;
    case "psychro": return <PsychrometricCalc />;
    case "coil": return <CoilSizingCalc />;
    case "condensate": return <CondensateDrainCalc />;
    case "ductarea": return <DuctAreaCalc />;
    case "capacity": return <DataTable headers={maxCfmHeaders} rows={maxCfmRows} searchable />;
    case "fittings": return (
      <>
        <p className="text-[11px] text-muted-foreground leading-relaxed mb-2">
          Approximate equivalent lengths for common duct fittings. Use with friction loss calculations for total system pressure drop.
        </p>
        <DataTable headers={fittingEqHeaders} rows={fittingEqRows} searchable />
      </>
    );
    case "room-cfm": return (
      <>
        <p className="text-[11px] text-muted-foreground leading-relaxed mb-2">
          Rule-of-thumb CFM per room type. Verify with Manual J (residential) or ASHRAE (commercial).
        </p>
        <DataTable headers={cfmRoomHeaders} rows={cfmRoomRows} searchable />
      </>
    );
    case "superheat": return <SuperheatCalculator />;
    case "subcooling": return <SubcoolingCalculator />;
    case "linesizing": return <LineSizingCalculator />;
    case "charge": return <ChargeEstimator />;
    case "tplookup": return <TempPressureLookup />;
    case "ptchart": return <div className="-mx-4"><DataTable headers={ptChartHeaders} rows={ptChartRows} searchable searchPlaceholder="Search P-T chart…" /></div>;
    case "linetbl": return <div className="-mx-4"><DataTable headers={lineSizingHeaders} rows={lineSizingRows} searchable searchPlaceholder="Search line sizes…" favoritesConfig={{ type: "duct", category: "❄️ Refrig. Lines", makeId: (row) => `refline-${row[0]}`, makeName: (row) => `${row[0]} ton`, makeDetail: (row) => `Suction ${row[1]} · Liquid ${row[2]} · HG ${row[3]}` }} /></div>;
    case "formulas": return <TradeFormulas trade="ductwork" />;
    default: return null;
  }
};

const Ductwork = () => (
  <TradeToolShell
    title="🌀 Ductwork"
    subtitle="Duct sizing, airflow, HVAC/R diagnostics & refrigerant tools"
    categories={DUCT_CATEGORIES}
    renderTool={renderTool}
  />
);

export default Ductwork;
